/**
 * The progression engine: given an exercise's set history, decides the next
 * session's prescription. Deterministic, client-side, pure — no DOM,
 * AppState or Supabase access. An LLM never produces a number here.
 *
 * Effort is inferred from performance, never self-reported (RPE is gone from
 * the product). The signal that replaces it is how a lifter's capacity holds up
 * across the sets of a session: 10/10/10/10 and 10/9/7/5 at the same load are
 * the same total reps and completely different stimuli — see `maxDecline`
 * and the `even_out` rule.
 *
 * ## What this rewrite fixed
 *
 * The first version of this engine read a session wrongly and then punished the
 * user for it. Concretely, on the app's own seeded demo history (a bench press
 * ramped 80×8, 85×6, 90×5, 90×4) it prescribed **72kg** — a 20% cut off a lift
 * the user had just completed — because:
 *
 * 1. It called `sets[0]` the "top set". For anyone who ramps up to a working
 *    weight, that is the *lightest* set of the session. Every heavier set then
 *    read as a failure, and the estimated 1RM was computed from the warm-up.
 *    Now the top set is the heaviest set, and lighter sets are understood as
 *    ramp-up and carried forward rather than flattened away.
 * 2. Any single set below the rep floor triggered a 10% load cut, with no
 *    history required. A first-ever session of 10/9/8/7 — ordinary fatigue on
 *    straight sets — was read as failure. Now only the *top* set falling short
 *    means the load is too heavy; a fade on the later sets is fatigue, and
 *    fatigue is answered by evening the sets out, not by cutting the load.
 * 3. Deloads compounded. The engine cut the load, the user complied, the
 *    lighter session read as "estimated strength trending down", and it cut
 *    again — 60 → 54 → 48.6 → 43.7, punishing a user who had *improved*
 *    session over session. Regression now requires that the latest session
 *    actually be worse than the one before it, so complying with a deload can
 *    never trigger another, and the load cut for a too-heavy lift is derived
 *    from the lifter's own estimated 1RM, which converges instead of spiralling.
 * 4. Drop-off looked only at the first and last set, so 10/4/4/10 read as a
 *    perfectly even session. It now measures the worst set against the best.
 * 5. It prescribed the fatigue pattern as the *target* ("80kg × 16/14/11/8"),
 *    institutionalising a fade as the goal. It now prescribes even sets holding
 *    total reps roughly constant.
 * 6. Set count could never change: it was whatever the last session happened to
 *    contain, so one interrupted two-set session froze the lifter at two sets
 *    forever — while `recommendSetDelta`, the only set-count logic in the
 *    codebase, was dead code no caller ever reached. Sets are now actually
 *    recommended, against the weekly volume landmarks.
 * 7. Nothing looked at the calendar. Six months off and it still said "add
 *    5kg". There is now a re-entry rule.
 * 8. The goal's rep range was enforced as a rule rather than offered as a
 *    default. A lifter deliberately training threes and sixes under a
 *    "hypertrophy" profile was told they had come up short every session, and
 *    walked down 100 → 95 → 90 → 85 for doing exactly what they intended. A rep
 *    count they hold cleanly across sessions is now read as their target and
 *    progressed by load — see `establishedRepTarget`.
 *
 * ## Rules, in the order they are tested
 *
 * | rule              | when                                        | answer                            |
 * |-------------------|---------------------------------------------|-----------------------------------|
 * | `cold_start`      | no usable history                           | prescribe nothing, say so         |
 * | `layoff`          | long gap since the last session             | ease the load back in             |
 * | `load_too_heavy`  | top set fell short of the rep floor         | load derived from estimated 1RM   |
 * | `below_goal_range`| under the goal's floor, but every set held  | hold; the lifter sets the target  |
 * | `add_load`        | every work set cleared the rep ceiling      | add one or two load steps         |
 * | `regression`      | capacity trending down *and* still falling  | ~10% cut, rebuild mid-range       |
 * | `stall`           | capacity flat for several sessions          | add a set, or ~10% cut at the cap |
 * | `even_out`        | capacity faded sharply across sets          | hold load, flatten the rep target |
 * | `push_back_sets`  | top set cleared the ceiling, later ones not | hold load, push the laggards      |
 * | `add_reps`        | default                                     | hold load, one more rep per set   |
 */

import { getRepRange, getRebuildReps } from './schemes.js';
import { getIncrementKg, getMaxLoadSteps } from './increments.js';
import { suppressDoubleJump, recommendSetDelta } from './volume.js';

/**
 * Fatigue thresholds, in *capacity* terms (see `maxDecline`) rather than raw
 * reps. Capacity compresses reps the way Epley does, so these numbers look
 * smaller than the rep-drop-off figures they replace: losing half your reps
 * (10 → 5) is a 12.5% capacity loss. `FATIGUE_HOLD` sits a little above the old
 * 35% rep drop-off so that ordinary straight-set fade — 12/10/9/8, which is
 * what four honest sets look like — is answered by pushing the back sets up
 * rather than by telling the lifter they fell apart. Reading fatigue in
 * capacity rather than reps is what stops a ramped session, where reps fall
 * because the load rose, from being mistaken for collapse at all.
 */
const FATIGUE_HOLD = 0.11;
const FATIGUE_CLEAN = 0.03;

const DELOAD_FACTOR = 0.9;
/** No single session may cut the load by more than this, whatever the arithmetic says. */
const MAX_LOAD_CUT = 0.2;
const STALL_SESSIONS = 3;
const REGRESSION_SESSIONS = 3;
/**
 * A trend has to clear the noise floor to count. The old engine deloaded on
 * *any* negative slope, so an estimated 1RM of 100 → 101 → 99 — three sessions
 * of a lifter holding steady — was read as decline. Expressed as a fraction of
 * the window's mean capacity, per session.
 */
const REGRESSION_SLOPE_PCT = 0.015;
/** Capacity within this fraction of where it started is "no progress", i.e. a stall. */
const STALL_NOISE_PCT = 0.005;
/**
 * Sessions of the same clean rep count needed before it is read as the lifter's
 * own target rather than as falling short of their goal's range.
 */
const REP_TARGET_SESSIONS = 2;
/** Days off after which the next session is a re-entry rather than a continuation. */
const LAYOFF_DAYS = 21;
/** How far reps-only equipment may run past rep_high before external load is suggested. */
const REPS_ONLY_EXTENDED_CAP = 6;
/** How many recent sessions decide the habitual set count. */
const SET_COUNT_WINDOW = 4;
const MIN_SETS = 1;
const MAX_SETS = 6;

/**
 * Loads that differ by less than this are the same load to a lifter.
 *
 * Strict equality is wrong here. An imperial user's weights survive a
 * kg↔lb round trip on every edit (80kg renders as 176.4lb and comes back as
 * 80.01kg), so `===` would see three identical sessions as three different ones
 * and silently disable stall detection for everyone not on kilograms. 50g is
 * far below any real plate and far above the round-trip error.
 */
const SAME_LOAD_EPSILON_KG = 0.05;

function sameLoad(a, b) {
  return Math.abs(a - b) < SAME_LOAD_EPSILON_KG;
}

function workingSets(session) {
  return (session?.sets || []).filter((s) => (s.set_type || 'working') === 'working' && (s.reps || 0) > 0);
}

/** Plain Epley e1RM — mirrors `window.calcE1RM` (core.js) so results agree with the rest of the app. */
export function e1rm(weightKg, reps) {
  if (!weightKg || weightKg <= 0) return 0;
  if (!reps || reps <= 1) return weightKg;
  return Math.round(weightKg * (1 + reps / 30) * 10) / 10;
}

/**
 * What one set says about the lifter's current capacity — Epley without the
 * rounding, and without `e1rm`'s hard zero for unloaded work.
 *
 * The exported `e1rm` has to keep matching the app's PR logic exactly, so it
 * stays as it is. This is the engine's internal yardstick, and it differs in
 * one way that matters: a bodyweight set scores as if the load were 1, so
 * calisthenics progress is comparable across sessions and reads on the same
 * fatigue scale as barbell work. The old engine scored every bodyweight set as
 * zero, which made its trend and stall detection inert for anyone training
 * without external load.
 */
function capacity(set) {
  const weight = set.weight_kg || 0;
  const reps = set.reps || 0;
  return (weight > 0 ? weight : 1) * (1 + reps / 30);
}

/**
 * The sharpest *decline* in capacity from any set to any later set of the
 * session, as a fraction. 0 = never went backwards, 1 = collapsed completely.
 *
 * Two decisions are load-bearing here. It measures the largest fall to a
 * *later* set, not the spread across the session, so capacity that only ever
 * rises is not fatigue. And it takes the worst fall anywhere rather than
 * first-against-last, so 10/4/4/10 — collapsing mid-session and rallying — is
 * caught, where the old first-vs-last measure scored it as a flawless session.
 *
 * It is fed the **work sets** only. Fatigue means the same load getting harder,
 * which is the one thing a set at a different load cannot tell you: a lighter
 * set after a heavy one is a back-off, and reading it as collapse told a lifter
 * who pressed 100×6 and then did 60×12 that their "sets faded badly". A single
 * work set therefore scores zero fatigue, which is correct — there is nothing to
 * compare it against.
 */
function maxDecline(capacities) {
  let best = 0;
  let worstFall = 0;
  capacities.forEach((value) => {
    if (value > best) best = value;
    if (best > 0) worstFall = Math.max(worstFall, (best - value) / best);
  });
  return worstFall;
}

/**
 * Everything the rules need from one session, read correctly.
 *
 * The distinction the old engine lacked: **work sets** are the sets at the
 * session's heaviest load, and **ramp sets** are the lighter ones leading up to
 * them. Progression happens on the work sets. Ramp sets are carried forward as
 * they were performed — they are the lifter's own warm-up structure, not
 * something to progress or to average into a decision.
 */
function readSession(session) {
  const sets = workingSets(session);
  if (!sets.length) return null;

  const topWeight = sets.reduce((max, s) => Math.max(max, s.weight_kg || 0), 0);
  const isWorkSet = (s) => sameLoad(s.weight_kg || 0, topWeight);
  const workSets = sets.filter(isWorkSet);
  const capacities = sets.map(capacity);
  const fatigue = maxDecline(workSets.map(capacity));

  return {
    date: session.date,
    sets,
    setCount: sets.length,
    topWeight,
    isWorkSet,
    workSets,
    workReps: workSets.map((s) => s.reps || 0),
    /** The best single set of the session — the honest "top set", heaviest first, most reps to break a tie. */
    topSetReps: workSets.reduce((best, s) => Math.max(best, s.reps || 0), 0),
    bestCapacity: Math.max(...capacities),
    fatigue,
    /** True when the session was ramped rather than run as straight sets. */
    isRamp: workSets.length < sets.length,
  };
}

/**
 * Rep-only drop-off across a session's sets: the worst set against the best,
 * as a fraction. Kept exported because it is the plainest statement of the idea
 * the product is built on, and it is what a reader wants to see in a test.
 */
export function computeDropOff(reps) {
  const usable = reps.filter((r) => r > 0);
  if (!usable.length) return 0;
  const best = Math.max(...usable);
  const worst = Math.min(...usable);
  return best ? Math.max(0, (best - worst) / best) : 0;
}

/** Least-squares slope of per-session capacity, oldest first. */
function capacitySlope(reads) {
  const ys = reads.map((r) => r.bestCapacity);
  const n = ys.length;
  if (n < 2) return 0;
  const meanX = (n - 1) / 2;
  const meanY = ys.reduce((a, b) => a + b, 0) / n;
  let num = 0;
  let den = 0;
  for (let i = 0; i < n; i += 1) {
    num += (i - meanX) * (ys[i] - meanY);
    den += (i - meanX) ** 2;
  }
  return den === 0 ? 0 : num / den;
}

function meanCapacity(reads) {
  if (!reads.length) return 0;
  return reads.reduce((sum, r) => sum + r.bestCapacity, 0) / reads.length;
}

/**
 * The latest session is at or near the best the lifter has managed recently.
 *
 * Guards the reading of a low rep count as a *choice*. Three clean sets of six
 * are a scheme when six is what this lifter always does, and a problem when they
 * were doing ten at the same weight a fortnight ago — the sets look identical,
 * and only the history tells them apart.
 */
function atRecentBest(reads) {
  const window = reads.slice(-(REP_TARGET_SESSIONS + 1));
  const best = Math.max(...window.map((r) => r.bestCapacity));
  return !best || reads[reads.length - 1].bestCapacity >= best * 0.99;
}

/**
 * The rep count the lifter is actually training at, when it sits below the one
 * their goal implies — or `null` when there is no such pattern.
 *
 * A goal's rep range is a *default* for someone with no established pattern, not
 * a rule to enforce against someone who has one. Without this, a lifter who
 * deliberately trains threes-and-sixes while their profile says "hypertrophy"
 * is told every single session that they came up short, and the load is cut to
 * chase a rep count they never intended: 100 → 95 → 90 → 85, session after
 * session, for doing exactly what they meant to do.
 *
 * What separates a chosen rep target from a load that is genuinely too heavy is
 * not the number — it is whether the lifter holds it. Even sets they repeat
 * across sessions are a scheme. Sets that fade away from the top one, or that
 * dropped to here from inside the goal's range, are a problem, and those still
 * fall through to `load_too_heavy`.
 */
function establishedRepTarget(reads, goalLow) {
  if (reads.length < REP_TARGET_SESSIONS) return null;
  // One session wider than the evidence required, so a *decline* into this rep
  // count reads as the decline it is: the older session sits inside the goal's
  // range and fails the test below.
  const window = reads.slice(-(REP_TARGET_SESSIONS + 1));
  const tops = window.map((r) => r.topSetReps);
  if (!tops.every((r) => r < goalLow)) return null;

  const lowest = Math.min(...tops);
  const highest = Math.max(...tops);
  if (highest - lowest > 1) return null;
  // Held across every work set, not faded to on the last one.
  if (!window.every((r) => Math.min(...r.workReps) >= highest - 1)) return null;
  return highest;
}

/**
 * Every session in the window was run at the same top load.
 *
 * Both load-cutting rules below require this, and that single condition is what
 * keeps the engine from reading its own advice as bad news — in either
 * direction:
 *
 * - **After a cut.** A lighter session always scores lower on capacity. Without
 *   this guard the engine cuts to 55, the lifter complies and hits every rep,
 *   the window slopes down, so it cuts to 50, and on into the floor. That was
 *   the original engine's worst behaviour: 60 → 54 → 48.6 → 43.7 for a lifter
 *   who was improving session over session.
 * - **After an increase.** Adding load resets the rep target to the floor of the
 *   range by design, so capacity flattens across the jump — Epley scores 65×12
 *   and 70×8 within 3% of each other. Without this guard, adding 5kg and
 *   climbing back up reads as a plateau, and the lifter gets deloaded two
 *   sessions after a genuine PR.
 *
 * What is left is an unambiguous pair of definitions. At one steady load,
 * capacity is a monotone function of reps, so `stall` means "the same reps at
 * the same weight, three sessions running" and `regression` means "fewer reps at
 * the same weight, and still falling". Neither can be triggered by a load change
 * the engine itself asked for.
 *
 * A lifter whose load is drifting down of their own accord is therefore not
 * deloaded again — they are already doing it, and `load_too_heavy` still sizes
 * the load off their own estimated 1RM the moment they miss the rep floor.
 * Telling a following deload apart from a genuine slide needs
 * `sets.prescribed_weight_kg` (see the note in `prescribe.js`); until that column
 * exists, this is the safe side to err on.
 */
function loadSteady(window) {
  const first = window[0].topWeight;
  return window.every((r) => sameLoad(r.topWeight, first));
}

/** Capacity trending down at a steady load, and the latest session still falling. */
function isRegressing(reads) {
  if (reads.length < REGRESSION_SESSIONS) return false;
  const window = reads.slice(-REGRESSION_SESSIONS);
  if (!loadSteady(window)) return false;
  const slope = capacitySlope(window);
  const noise = meanCapacity(window) * REGRESSION_SLOPE_PCT;
  if (slope >= -noise) return false;
  const last = window[window.length - 1];
  const previous = window[window.length - 2];
  return last.bestCapacity < previous.bestCapacity;
}

/**
 * Capacity has gone nowhere across the window.
 *
 * The old stall test required the rep arrays of three sessions to be *identical*
 * — 10/10/10 then 10/10/9 then 10/10/10 was not a stall — so the rule that was
 * meant to break plateaus almost never fired, while the noise-triggered
 * regression rule fired constantly. Comparing capacity catches a real plateau
 * whatever shape the sets took.
 */
/**
 * The same work, three sessions running, for the same result.
 *
 * "The same work" means the same load *and* the same number of sets. Requiring
 * the set count to hold is what paces the intervention: the stall rule answers a
 * mid-range plateau by adding a set, and adding a set does not move the best set
 * of a session, so without this the very next session would read as stalled
 * again and ask for another. The lifter would be handed a set a week until they
 * hit the cap. Change the work and the clock starts over.
 */
function isStalled(reads) {
  if (reads.length < STALL_SESSIONS) return false;
  const window = reads.slice(-STALL_SESSIONS);
  if (!loadSteady(window)) return false;
  if (!window.every((r) => r.setCount === window[0].setCount)) return false;
  const first = window[0].bestCapacity;
  if (!first) return false;
  const best = Math.max(...window.map((r) => r.bestCapacity));
  return best <= first * (1 + STALL_NOISE_PCT);
}

function daysBetween(fromIso, toMs) {
  const from = new Date(fromIso).getTime();
  if (!Number.isFinite(from) || !Number.isFinite(toMs)) return null;
  return (toMs - from) / 86400000;
}

/** Epley, inverted: the load that should yield `reps` for a lifter at this capacity. */
function loadForReps(bestCapacity, reps) {
  return bestCapacity / (1 + reps / 30);
}

function clamp(value, low, high) {
  return Math.min(high, Math.max(low, value));
}

function round2(value) {
  return Math.round(value * 100) / 100;
}

/**
 * Turn a load *cut* into whole increments of the lifter's own plates, floored
 * so a single session can never crater the load.
 *
 * Snapping matters as much as the size of the cut: 10% off 60kg is 54kg, which
 * is not a weight anyone can load on a barbell. A prescription the lifter
 * cannot actually set up is one they have to overrule, and a prescription
 * overruled every session is one they stop reading.
 */
function cutToIncrement(topWeight, desiredWeight, incrementKg) {
  if (!incrementKg) return round2(Math.max(desiredWeight, topWeight * (1 - MAX_LOAD_CUT)));
  // The cut cap limits how many *steps* come off, never the weight they land on.
  // Capping the weight instead let the cap win the last word and put the lifter
  // somewhere off their own grid: 20% under a 45lb bar is 36lb, and under 20lb
  // dumbbells it is 16lb. At light loads one step is already more than the cap
  // allows, and one step is also the smallest change that exists — so the floor
  // yields to the plates rather than the other way round.
  const wanted = Math.round((topWeight - desiredWeight) / incrementKg);
  const allowed = Math.floor((topWeight * MAX_LOAD_CUT) / incrementKg);
  const steps = Math.max(1, Math.min(wanted, Math.max(1, allowed)));
  return round2(Math.max(0, topWeight - steps * incrementKg));
}

/** The standard ~10% back-off, snapped to real plates. */
function deloadTo(topWeight, incrementKg) {
  return cutToIncrement(topWeight, topWeight * DELOAD_FACTOR, incrementKg);
}

/**
 * Build a prescription: every set keeps its own load, shifted by one uniform
 * delta, with a per-set rep target.
 *
 * Per-set loads are what preserves a ramp. The old engine wrote a single weight
 * across every set, so a lifter who worked up to 90kg was handed four sets at
 * their 80kg opener — and a plate step added to a ramp lands on every rung of
 * it, which is exactly how a ramp is supposed to move.
 */
function prescribe(read, { loadDeltaKg = 0, reps, incrementKg }) {
  return read.sets.map((set, i) => {
    const own = set.weight_kg || 0;
    return {
      weightKg: round2(shiftLoad(own, loadDeltaKg, incrementKg)),
      reps: Math.max(1, Math.round(reps[i])),
    };
  });
}

/**
 * One set's load, moved by the session's delta and kept on the grid its own
 * plates make.
 *
 * A cut is sized against the *top* set, so on a ramp with a very light opening
 * rung — an empty bar under a heavy top set — the same delta could take that
 * rung to or below zero. No loaded set drops below half its own weight, and the
 * shortened cut is still whole steps, so the rung lands on a weight the lifter
 * can build rather than on whatever half of it happens to be. A realistic ramp
 * never reaches this floor at all.
 */
function shiftLoad(own, loadDeltaKg, incrementKg) {
  const moved = own + loadDeltaKg;
  if (own <= 0 || moved >= own / 2) return Math.max(0, moved);
  if (!incrementKg) return own / 2;
  const affordable = Math.floor(own / (2 * incrementKg));
  return own - affordable * incrementKg;
}

/** Flat rep target across every set — used whenever the load changes. */
function flatReps(read, value) {
  return read.sets.map(() => value);
}

/**
 * Rep targets that hold the ramp-up sets where they were and move only the work
 * sets, via `mapWork`.
 */
function workReps(read, mapWork) {
  let workIndex = 0;
  return read.sets.map((set) => {
    if (!read.isWorkSet(set)) return set.reps || 0;
    const value = mapWork(set.reps || 0, workIndex);
    workIndex += 1;
    return value;
  });
}

/**
 * Grow or trim a prescription to `targetCount` sets. An added set repeats the
 * last set's target; a trimmed one comes off the end.
 */
function resize(prescription, targetCount) {
  if (targetCount === prescription.length) return prescription;

  if (targetCount < prescription.length) {
    // Trimming takes work off the end — except where that would take the last
    // set at the top load with it. A two-set ramp cut to one set was handing the
    // lifter their warm-up rung and throwing the working set away.
    const top = Math.max(...prescription.map((s) => s.weightKg));
    let kept = prescription;
    while (kept.length > targetCount) {
      const withoutLast = kept.slice(0, -1);
      kept = withoutLast.some((s) => s.weightKg >= top) ? withoutLast : kept.slice(1);
    }
    return kept;
  }

  const last = prescription[prescription.length - 1];
  const extra = Array.from({ length: targetCount - prescription.length }, () => ({ ...last }));
  return [...prescription, ...extra];
}

/**
 * The set count the lifter habitually does, which is *not* simply the last
 * session's.
 *
 * One session cut short — a gym closing, a phone dying mid-workout — used to
 * become the lifter's set count for good, because the engine read the number
 * straight off the most recent session. Taking the larger of the last session
 * and the recent median makes a short session a blip; capping at median + 1
 * keeps one unusually long session from sticking either.
 */
function habitualSetCount(reads) {
  const counts = reads.slice(-SET_COUNT_WINDOW).map((r) => r.setCount);
  const sorted = [...counts].sort((a, b) => a - b);
  const median = sorted[Math.floor(sorted.length / 2)];
  const last = counts[counts.length - 1];
  return clamp(Math.min(Math.max(last, median), median + 1), MIN_SETS, MAX_SETS);
}

const GROUP_LABELS = {
  chest: 'chest',
  back: 'back',
  shoulders: 'shoulder',
  legs: 'leg',
  arms: 'arm',
  core: 'core',
  full_body: 'full-body',
};

/**
 * The set-count half of the recommendation, which the old engine computed and
 * then threw away: `recommendSetDelta` existed, was tested, and was never
 * called by anything.
 *
 * Volume landmarks only ever move the set count by one, and only in the
 * directions they are good for: add a set when the muscle group is under the
 * minimum that drives adaptation, drop one when it is at the ceiling of what
 * can be recovered from. A set is never *added* in the same session as a load
 * increase, a load cut or a fatigue hold — one variable at a time, or neither
 * the lifter nor the engine can tell which change did what.
 */
function planSetCount(baseCount, { muscleGroup, weeklySetsForMuscleGroup, volumeLandmarks, mayAdd, force }) {
  const landmarkDelta = recommendSetDelta(weeklySetsForMuscleGroup, volumeLandmarks);
  const delta = force || landmarkDelta;
  const unchanged = { setCount: baseCount, setCountDelta: 0, setNote: null };
  if (delta === 0 || (delta > 0 && !force && !mayAdd)) return unchanged;

  const setCount = clamp(baseCount + delta, MIN_SETS, MAX_SETS);
  if (setCount === baseCount) return unchanged;

  const group = GROUP_LABELS[muscleGroup] || 'this muscle group';
  // A set the rule itself asked for is already explained by that rule's reason;
  // only a landmark-driven change needs its own sentence.
  const setNote = force
    ? null
    : setCount > baseCount
      ? `Adding a ${setCount}th set — your weekly ${group} volume is below what drives growth.`
      : `Dropping to ${setCount} sets — ${group} volume is at the ceiling of what you can recover from.`;
  return { setCount, setCountDelta: setCount - baseCount, setNote };
}

/** Whether there is room for another set: under the recoverable ceiling, and not already at the cap. */
function canAddSet(baseCount, weeklySetsForMuscleGroup, landmarks) {
  if (baseCount >= MAX_SETS) return false;
  if (!landmarks || typeof weeklySetsForMuscleGroup !== 'number') return true;
  return typeof landmarks.mrv !== 'number' || weeklySetsForMuscleGroup < landmarks.mrv;
}

function result(fields) {
  return {
    hasHistory: true,
    needsExternalLoad: false,
    setCountDelta: 0,
    setNote: null,
    ...fields,
  };
}

/**
 * @param {object} input
 * @param {string} input.goal - `profiles.goal` (fitness_goal enum)
 * @param {string} input.equipment - `exercises.equipment`
 * @param {string} [input.exerciseType] - `exercises.exercise_type`
 * @param {string} [input.muscleGroup] - `exercises.muscle_group`
 * @param {string} [input.unitSystem] - 'metric' | 'imperial'
 * @param {Array} input.sessions - completed sessions for this exercise, oldest first.
 *   Each session: `{ date?, sets: [{ weight_kg, reps, set_type }] }`.
 * @param {number} [input.weeklySetsForMuscleGroup] - drives the set-count recommendation
 * @param {object} [input.volumeLandmarks] - `{ mev, mav, mrv }`
 * @param {number} [input.now] - epoch ms, for the layoff rule. Defaults to now.
 * @returns {object} `{ hasHistory, rule, reason, setNote, setCount, setCountDelta,
 *   prescription, needsExternalLoad, loadDeltaKg? }`
 */
export function prescribeNextSession(input) {
  const {
    goal,
    equipment,
    exerciseType,
    muscleGroup,
    unitSystem = 'metric',
    sessions = [],
    weeklySetsForMuscleGroup,
    volumeLandmarks,
    now = Date.now(),
  } = input;

  const reads = sessions.map(readSession).filter(Boolean);

  if (!reads.length) {
    return {
      hasHistory: false,
      rule: 'cold_start',
      reason: "Log a set — we'll calibrate from here.",
      setNote: null,
      setCount: null,
      setCountDelta: 0,
      prescription: null,
      needsExternalLoad: false,
    };
  }

  const goalRange = getRepRange(goal, exerciseType);
  // A rep count the lifter repeats cleanly *is* the target, whatever the goal
  // says. Collapsing the range onto it turns the double progression into the
  // linear one a fixed rep scheme actually wants: hit your number, add load.
  const ownRepTarget = establishedRepTarget(reads, goalRange.low);
  const { low: repLow, high: repHigh } = ownRepTarget == null
    ? goalRange
    : { low: ownRepTarget, high: ownRepTarget };
  const rebuildReps = clamp(getRebuildReps({ low: repLow, high: repHigh }), repLow, repHigh);
  const last = reads[reads.length - 1];
  const { fatigue } = last;
  const incrementKg = getIncrementKg({ equipment, muscleGroup, unitSystem });
  const maxLoadSteps = getMaxLoadSteps({ equipment, muscleGroup, unitSystem });
  // No load to add or cut — either the equipment has none, or the lifter logged
  // reps without a weight. Treating the second case as loadable prescribed a 5kg
  // barbell bench press off a session recorded at zero.
  const repsOnly = incrementKg == null || last.topWeight <= 0;
  const baseSetCount = habitualSetCount(reads);

  // Every rule prescribes for the last session, and every one of them has to
  // land on the increments this exercise's plates or stack actually make.
  const prescribeSets = (fields) => prescribe(last, { ...fields, incrementKg });

  // Whichever rule fires, the set count is decided once, at the end.
  const finish = (fields, { mayAddSet = false, forceSetCountDelta = 0 } = {}) => {
    const plan = planSetCount(baseSetCount, {
      muscleGroup,
      weeklySetsForMuscleGroup,
      volumeLandmarks,
      mayAdd: mayAddSet,
      force: forceSetCountDelta,
    });
    return result({
      ...fields,
      setCount: plan.setCount,
      setCountDelta: plan.setCountDelta,
      setNote: plan.setNote,
      prescription: resize(fields.prescription, plan.setCount),
    });
  };

  // `layoff` — back after a long break. Nothing else in the history means what
  // it usually means, so no other rule gets to read it.
  const gapDays = last.date ? daysBetween(last.date, now) : null;
  if (gapDays != null && gapDays >= LAYOFF_DAYS) {
    const weeks = Math.round(gapDays / 7);
    const weightKg = repsOnly ? last.topWeight : deloadTo(last.topWeight, incrementKg);
    return finish({
      rule: 'layoff',
      reason: `First session back after ${weeks} weeks — easing the load in before adding to it.`,
      prescription: prescribeSets({
        loadDeltaKg: weightKg - last.topWeight,
        reps: flatReps(last, rebuildReps),
      }),
    });
  }

  // `load_too_heavy` — the top set fell short of the rep floor, i.e. the load
  // was too heavy even fresh. Note what this does *not* fire on: a fade on the
  // later sets. That is fatigue, and `even_out` handles it.
  //
  // The new load comes from the lifter's own estimated 1RM rather than a flat
  // 10% guess, which is what makes this rule converge. Once they are hitting the
  // floor, the derived load equals the load they are already using, so it
  // settles instead of ratcheting down.
  if (last.topSetReps < repLow) {
    // `below_goal_range` — under the goal's floor, but the lifter held the number
    // on every work set and is not sliding. A load that is genuinely too heavy
    // shows itself in failure or fade; a rep count on its own proves nothing
    // about the load, and cutting on that alone is what marched a deliberate
    // 6-rep lifter down 5kg a session.
    //
    // The bar is higher here than in `establishedRepTarget` — every work set at
    // the number, not within one of it — because there is far less evidence: a
    // single session, where a fade is the only thing that could tell a chosen
    // scheme from a load being ground out. One clean session is not enough to
    // start adding load from either, so it holds and says why; a second
    // establishes the target and `add_load` takes over.
    const heldEveryWorkSet = Math.min(...last.workReps) >= last.topSetReps;
    if (heldEveryWorkSet && atRecentBest(reads) && !repsOnly) {
      return finish({
        rule: 'below_goal_range',
        reason: `Even sets of ${last.topSetReps} — nothing here says the load is too heavy, so it holds. Your goal aims at ${goalRange.low}–${goalRange.high} reps; keep training at ${last.topSetReps} and we'll add load at ${last.topSetReps} instead.`,
        prescription: prescribeSets({ reps: workReps(last, () => last.topSetReps) }),
      });
    }
    if (repsOnly) {
      return finish({
        rule: 'load_too_heavy',
        reason: `Short of ${repLow} reps even on your best set — same set-up, building toward ${repLow} clean.`,
        prescription: prescribeSets({ reps: flatReps(last, Math.max(1, last.topSetReps)) }),
      });
    }
    const desired = loadForReps(last.bestCapacity, repLow);
    const weightKg = cutToIncrement(last.topWeight, desired, incrementKg);
    return finish({
      rule: 'load_too_heavy',
      reason: `Your best set was ${last.topSetReps} reps, short of the ${repLow}–${repHigh} target — this load should put you back in range.`,
      loadDeltaKg: round2(weightKg - last.topWeight),
      prescription: prescribeSets({
        loadDeltaKg: weightKg - last.topWeight,
        reps: flatReps(last, repLow),
      }),
    });
  }

  // `add_load` — every work set cleared the top of the rep range, so the load is
  // the thing holding you back. Tested ahead of every rule that cuts load or
  // holds it: if even your worst work set is at the ceiling of the range, the
  // load is too light, whatever the session felt like and whatever the trend
  // across sessions says. A hard session still only earns a single step.
  const clearedAllWorkSets = last.workReps.every((r) => r >= repHigh);
  if (clearedAllWorkSets) {
    // A double step is earned by reps to spare inside the range. A lifter on a
    // fixed rep target has no spare reps to show — their number is the number —
    // so a fixed target progresses one step at a time.
    const clean = fatigue <= FATIGUE_CLEAN && ownRepTarget == null;

    if (repsOnly) {
      // Reps-only equipment: "adding load" means adding reps past rep_high. Reps
      // have no plate granularity to respect, so a clean session takes two of
      // them and `getMaxLoadSteps` — which is about what a gym can load — does
      // not apply.
      const extendedCap = repHigh + REPS_ONLY_EXTENDED_CAP;
      const bumped = Math.min(extendedCap, last.topSetReps + (clean ? 2 : 1));
      const atCap = bumped >= extendedCap;
      return finish({
        rule: 'add_load',
        reason: atCap
          ? `Cleared ${repHigh}+ reps with no equipment to add load — time to add external weight.`
          : `Cleared the top of the rep range — pushing reps to ${bumped}.`,
        prescription: prescribeSets({ reps: flatReps(last, bumped) }),
        needsExternalLoad: atCap,
      });
    }

    // The other half of the double-step decision: equipment whose single step is
    // already at the unit system's ceiling (a 10lb bar, a 5kg pin) never doubles,
    // so no session recommends a jump outside the granularity every other number
    // lands on.
    const steps = clean ? maxLoadSteps : 1;
    const cappedSteps = suppressDoubleJump(steps, weeklySetsForMuscleGroup, volumeLandmarks);
    const loadDeltaKg = cappedSteps * incrementKg;
    // `{load}` is substituted by format.js in the reader's own unit. The engine
    // reasons in kg only and must not put a unit in front of a user — a target
    // shown in pounds beside a reason quoting kilograms reads as a bug, because
    // it is one.
    return finish({
      rule: 'add_load',
      reason: ownRepTarget != null
        ? `Hit ${ownRepTarget} reps on every set — adding {load} and holding the target at ${ownRepTarget}.`
        : cappedSteps >= 2
          ? `Cleared every set with reps to spare — adding {load} and resetting to ${repLow} reps.`
          : `Cleared the top of the rep range — adding {load} and resetting to ${repLow} reps.`,
      loadDeltaKg,
      prescription: prescribeSets({
        loadDeltaKg,
        // The ramp-up sets move with the load but keep their own rep targets;
        // only the work sets reset to the floor of the range.
        reps: workReps(last, () => repLow),
      }),
    });
  }

  // `regression` — genuinely going backwards, not merely lighter than last week
  // because we said so.
  if (!repsOnly && isRegressing(reads)) {
    const weightKg = deloadTo(last.topWeight, incrementKg);
    return finish({
      rule: 'regression',
      reason: `Estimated strength has slipped for ${REGRESSION_SESSIONS} sessions — backing the load off ~10% and rebuilding from ${rebuildReps} reps.`,
      loadDeltaKg: round2(weightKg - last.topWeight),
      prescription: prescribeSets({
        loadDeltaKg: weightKg - last.topWeight,
        reps: flatReps(last, rebuildReps),
      }),
    });
  }

  // `stall` — capacity flat for several sessions running.
  //
  // Which lever is stuck decides the answer. Below the top of the rep range the
  // lifter still has reps to win, and cutting the load there just hands back
  // ground they will have to retake — the honest answer is more work, so the
  // load and rep target hold and a set goes on. Only once they are at the
  // ceiling of the range, with nowhere left to add reps, is the load itself
  // what has to move.
  if (isStalled(reads)) {
    // A lifter on a fixed rep target is at the "ceiling" by definition — their
    // range has no room in it at all — so reading it that way would send every
    // stall of theirs straight to a load cut and skip the volume lever entirely.
    // Their number is not what is stuck; the work around it is.
    const atRepCeiling = ownRepTarget == null && last.topSetReps >= repHigh;
    if (!atRepCeiling && canAddSet(baseSetCount, weeklySetsForMuscleGroup, volumeLandmarks)) {
      return finish({
        rule: 'stall',
        reason: `Stuck on the same reps for ${STALL_SESSIONS} sessions with room left in the range — same load, one more set to drive it.`,
        // The target is the number they hold, not a replay of the last session:
        // carrying a missed set forward would make the miss the goal.
        prescription: prescribeSets({ reps: workReps(last, () => last.topSetReps) }),
      }, { forceSetCountDelta: 1 });
    }
    if (!repsOnly) {
      const weightKg = deloadTo(last.topWeight, incrementKg);
      return finish({
        rule: 'stall',
        reason: `No progress for ${STALL_SESSIONS} sessions — backing the load off ~10% to run at it again with room to grow.`,
        loadDeltaKg: round2(weightKg - last.topWeight),
        prescription: prescribeSets({
          loadDeltaKg: weightKg - last.topWeight,
          reps: flatReps(last, rebuildReps),
        }),
      });
    }
  }

  // `even_out` — capacity fell away sharply across the session. The lifter is
  // fatigue-limited, not load-limited, so the load holds and the rep target goes
  // flat at roughly the same total reps. Prescribing the fade back to them, as
  // the old engine did, made a target out of running out of gas.
  // Two work sets minimum: "even out your sets" is not advice you can give about
  // one of them, and with a single work set there is no fatigue reading anyway.
  if (fatigue > FATIGUE_HOLD && last.workSets.length >= 2) {
    const mean = last.workReps.reduce((a, b) => a + b, 0) / last.workReps.length;
    const target = clamp(Math.round(mean), repLow, repHigh);
    return finish({
      rule: 'even_out',
      reason: `Your sets faded badly — same load, but even sets of ${target}. Leave a rep in reserve early so the last set holds up.`,
      prescription: prescribeSets({ reps: workReps(last, () => target) }),
    });
  }

  // `push_back_sets` — the top set cleared the ceiling but the later work sets
  // did not. Close the gap before adding load.
  if (last.topSetReps >= repHigh) {
    return finish({
      rule: 'push_back_sets',
      reason: 'Your best set is at the top of the range — same load, bring the rest of the sets up to it.',
      prescription: prescribeSets({
        // Anything already at the ceiling holds; every work set below it moves up
        // one. Keyed on the reps rather than the set's position, because the best
        // set of a session is not always the first one performed.
        reps: workReps(last, (reps) => (reps >= repHigh ? reps : Math.min(repHigh, reps + 1))),
      }),
    }, { mayAddSet: true });
  }

  // `add_reps` — the default. Chip away a rep at a time, up to the top of the
  // range, and never ask for less than was already done.
  return finish({
    rule: 'add_reps',
    reason: 'Same load, one more rep on every working set.',
    prescription: prescribeSets({
      reps: workReps(last, (reps) => Math.max(reps, Math.min(repHigh, reps + 1))),
    }),
  }, { mayAddSet: true });
}
